import {
    t,
    MAX_INSTAGRAM_CAPTION_CHARACTER_LENGTH,
    MAX_INSTAGRAM_HASHTAGS_COUNT,
    MAX_INSTAGRAM_SCHEDULED_DAYS,
} from '@deltasierra/shared';

import { FormikConfig, FormikErrors } from 'formik';
import React from 'react';
import { DateTime, DurationObjectUnits } from 'luxon';
import { FormikValidateFunction } from '../../../typings/formik-validate-function';
import { countHashtagsInString } from '../common/count-hashtags-in-string';
import {
    buildCaption,
    buildFirstComment,
    buildHashtagString,
    BuildInstagramDirectPublishDataOptions,
    getCaptionWithTermsAndConditions,
} from './build-instagram-direct-publish-data';
import { InstagramDirectFormValues, HashtagInsertType } from './instagram-direct-form-values';

type useValidatedInstagramDirectFormOptions = BuildInstagramDirectPublishDataOptions & { date: Date | undefined };

export function useValidatedInstagramDirectForm({
    date,
    requiredHashtags,
    termsAndConditionsText,
    termsAndConditionsUrl,
}: useValidatedInstagramDirectFormOptions): Pick<
    FormikConfig<InstagramDirectFormValues>,
    'initialValues' | 'validate'
> {
    const initialValues: InstagramDirectFormValues = {
        caption: '',
        hashtags: [],
        hashtagsInsertType: 'comment' as HashtagInsertType,
        scheduledTime: date || null,
    };

    const validate = React.useCallback<FormikValidateFunction<InstagramDirectFormValues>>(
        // eslint-disable-next-line max-statements
        ({ caption, hashtags, hashtagsInsertType, scheduledTime }) => {
            const errors: FormikErrors<InstagramDirectFormValues> = {};

            const hashtagsString = buildHashtagString(hashtags);
            const hashtagsCount = hashtagsString ? countHashtagsInString(hashtagsString) : 0;

            const captionWithTermsAndConditions = getCaptionWithTermsAndConditions({
                baseCaption: caption,
                termsAndConditionsText,
                termsAndConditionsUrl,
            });

            const finalCaption = buildCaption({
                baseCaption: caption,
                hashtagsInsertType,
                hashtagsString,
                termsAndConditionsText,
                termsAndConditionsUrl,
            });

            const firstComment = buildFirstComment({
                hashtagsInsertType,
                hashtagsString,
            });

            // If we have a large caption we error here
            if (captionWithTermsAndConditions.length > MAX_INSTAGRAM_CAPTION_CHARACTER_LENGTH) {
                errors.caption = t('ERRORS.INSTAGRAM_ERRORS.MAX_CHARACTER_LIMIT_FOR_CAPTION_REACHED');
            }

            if (
                // If we have a hashtag string
                hashtagsString &&
                hashtagsString.length > 0 &&
                // And the caption is over the limit, it could be because of the hashtags
                ((hashtagsInsertType === 'caption' && finalCaption.length > MAX_INSTAGRAM_CAPTION_CHARACTER_LENGTH) ||
                    // OR the comment is over the limit, it could be because of the hashtags
                    (hashtagsInsertType === 'comment' &&
                        firstComment &&
                        firstComment.length > MAX_INSTAGRAM_CAPTION_CHARACTER_LENGTH))
            ) {
                errors.hashtags = t('BUILD.PUBLISH.HASHTAGS.ERROR_LIMIT_HASHTAG_LENGTH');
            }

            // If we have too many hashtags because of the caption
            if (
                (hashtagsInsertType === 'caption' &&
                    countHashtagsInString(captionWithTermsAndConditions) + hashtagsCount >
                        MAX_INSTAGRAM_HASHTAGS_COUNT) ||
                countHashtagsInString(captionWithTermsAndConditions) > MAX_INSTAGRAM_HASHTAGS_COUNT
            ) {
                errors.caption = t('BUILD.PUBLISH.HASHTAGS.ERROR_LIMIT_HASHTAG_COUNT', {
                    count: MAX_INSTAGRAM_HASHTAGS_COUNT,
                });
            }

            // If we have too many hashtags
            if (hashtagsCount > MAX_INSTAGRAM_HASHTAGS_COUNT) {
                errors.hashtags = t('BUILD.PUBLISH.HASHTAGS.ERROR_LIMIT_HASHTAG_COUNT', {
                    count: MAX_INSTAGRAM_HASHTAGS_COUNT,
                });
            }

            // Check Meta scheduling limits and date validity
            if (scheduledTime) {
                const scheduledTimeObject = new Date(scheduledTime);
                const luxonScheduledTime = DateTime.fromJSDate(scheduledTimeObject);
                if (!luxonScheduledTime.isValid) {
                    errors.scheduledTime = 'Invalid date';
                }

                const diff: DurationObjectUnits = luxonScheduledTime.diffNow('days').toObject();
                const isValidFacebookJobDate = diff.days ? diff.days < MAX_INSTAGRAM_SCHEDULED_DAYS : false;
                if (!isValidFacebookJobDate) {
                    errors.scheduledTime = t(
                        'BUILD.PUBLISH.INSTAGRAM.SCHEDULED_INSTAGRAM_POST_MUST_BE_NO_MORE_THAN_75_DAYS_IN_FUTURE',
                    );
                }

                const luxonCurrentTime = DateTime.now();
                const isPastDate = luxonScheduledTime < luxonCurrentTime;
                if (isPastDate) {
                    errors.scheduledTime = t('BUILD.PUBLISH.INSTAGRAM.SCHEDULED_POST_MUST_BE_AT_LEAST_MINS_IN_FUTURE', {
                        minutes: 15,
                    });
                }
            }

            return errors;
        },
        [termsAndConditionsText, termsAndConditionsUrl],
    );

    return {
        initialValues,
        validate,
    };
}
